import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Clock, Plus, Trash2, Lock, Unlock, 
  AlertCircle, CheckCircle, Loader2, CalendarDays,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, startOfWeek, addWeeks, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse datetime strings as Paris local time (strip Z suffix if present)
const parseLocalTime = (dtStr) => {
  if (!dtStr) return new Date();
  // Remove Z or timezone info to treat as local time
  const cleaned = dtStr.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');
  return parseISO(cleaned);
};

export const AvailabilityManager = ({ adminToken }) => {
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  // Form state for adding blocked slot
  const [newSlot, setNewSlot] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '08:00',
    endTime: '10:00',
    reason: '',
    isFullDay: false
  });

  const getAuthHeader = () => ({
    Authorization: `Bearer ${adminToken}`
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 7);
      
      const [slotsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/availability/blocked`, {
          headers: getAuthHeader(),
          params: {
            start_date: format(weekStart, "yyyy-MM-dd'T'00:00:00"),
            end_date: format(weekEnd, "yyyy-MM-dd'T'23:59:59")
          }
        }),
        axios.get(`${API}/admin/bookings`, {
          headers: getAuthHeader(),
          params: { limit: 100 }
        })
      ]);
      
      setBlockedSlots(slotsRes.data.blocked_slots || []);
      // Filter bookings for current week
      const weekBookings = (bookingsRes.data.bookings || []).filter(b => {
        const bookingDate = new Date(b.pickup_datetime);
        return bookingDate >= weekStart && bookingDate < weekEnd;
      });
      setBookings(weekBookings);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [adminToken, currentWeek]);

  useEffect(() => {
    if (adminToken) {
      fetchData();
    }
  }, [adminToken, fetchData]);

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      let startDatetime, endDatetime;
      
      // IMPORTANT: Use naive datetime strings (no Z suffix) - all times are Paris local time
      if (newSlot.isFullDay) {
        startDatetime = `${newSlot.date}T00:00:00`;
        endDatetime = `${newSlot.date}T23:59:59`;
      } else {
        startDatetime = `${newSlot.date}T${newSlot.startTime}:00`;
        endDatetime = `${newSlot.date}T${newSlot.endTime}:00`;
      }
      
      await axios.post(`${API}/admin/availability/block`, {
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        reason: newSlot.reason || 'Indisponibilité manuelle',
        is_full_day: newSlot.isFullDay
      }, { headers: getAuthHeader() });
      
      toast.success('Créneau bloqué avec succès');
      setShowAddModal(false);
      setNewSlot({
        date: format(new Date(), 'yyyy-MM-dd'),
        startTime: '08:00',
        endTime: '10:00',
        reason: '',
        isFullDay: false
      });
      fetchData();
    } catch (error) {
      console.error('Error blocking slot:', error);
      toast.error('Erreur lors du blocage');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Voulez-vous vraiment débloquer ce créneau ?')) return;
    
    try {
      await axios.delete(`${API}/admin/availability/block/${slotId}`, {
        headers: getAuthHeader()
      });
      toast.success('Créneau débloqué');
      fetchData();
    } catch (error) {
      console.error('Error deleting slot:', error);
      toast.error('Erreur lors du déblocage');
    }
  };

  // Generate week days
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Get slots/bookings for a specific day
  const getDayData = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    
    const daySlots = blockedSlots.filter(slot => {
      const slotDate = slot.start_datetime.split('T')[0];
      return slotDate === dayStr;
    });
    
    const dayBookings = bookings.filter(booking => {
      const bookingDate = booking.pickup_datetime.split('T')[0];
      return bookingDate === dayStr;
    });
    
    return { slots: daySlots, bookings: dayBookings };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white font-['Outfit']">
            Gestion des disponibilités
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Les réservations confirmées bloquent automatiquement 1h30 avant et après
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-emerald-500 text-black hover:bg-emerald-400"
          data-testid="add-blocked-slot-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Bloquer un créneau
        </Button>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentWeek(addWeeks(currentWeek, -1))}
          className="border-zinc-700 text-zinc-300"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-white font-medium">
          Semaine du {format(weekStart, 'd MMMM yyyy', { locale: fr })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
          className="border-zinc-700 text-zinc-300"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 divide-x divide-zinc-800">
          {weekDays.map((day, idx) => {
            const { slots, bookings: dayBookings } = getDayData(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <div key={idx} className="min-h-[200px]">
                {/* Day Header */}
                <div className={`p-3 text-center border-b border-zinc-800 ${isToday ? 'bg-emerald-500/10' : 'bg-zinc-800/30'}`}>
                  <div className={`text-xs uppercase ${isToday ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {format(day, 'EEE', { locale: fr })}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? 'text-emerald-400' : 'text-white'}`}>
                    {format(day, 'd')}
                  </div>
                </div>
                
                {/* Day Content */}
                <div className="p-2 space-y-2">
                  {/* Bookings */}
                  {dayBookings.map(booking => (
                    <div
                      key={booking.id}
                      className={`p-2 rounded text-xs ${
                        booking.status === 'confirmed' 
                          ? 'bg-emerald-500/20 border border-emerald-500/30' 
                          : 'bg-yellow-500/20 border border-yellow-500/30'
                      }`}
                    >
                      <div className="flex items-center gap-1 text-white font-medium">
                        <Clock className="w-3 h-3" />
                        {format(parseLocalTime(booking.pickup_datetime), 'HH:mm')}
                      </div>
                      <div className="text-zinc-400 truncate mt-1">
                        {booking.pickup_address?.split(',')[0]}
                      </div>
                      <div className={`mt-1 ${booking.status === 'confirmed' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                        {booking.status === 'confirmed' ? '✓ Confirmée' : '⏳ En attente'}
                      </div>
                    </div>
                  ))}
                  
                  {/* Blocked Slots */}
                  {slots.map(slot => (
                    <div
                      key={slot.id}
                      className={`p-2 rounded text-xs ${
                        slot.is_automatic 
                          ? 'bg-blue-500/20 border border-blue-500/30' 
                          : 'bg-red-500/20 border border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-white font-medium">
                          <Lock className="w-3 h-3" />
                          {slot.is_full_day ? 'Journée' : 
                            `${format(parseLocalTime(slot.start_datetime), 'HH:mm')} - ${format(parseLocalTime(slot.end_datetime), 'HH:mm')}`
                          }
                        </div>
                        {!slot.is_automatic && (
                          <button
                            onClick={() => handleDeleteSlot(slot.id)}
                            className="text-red-400 hover:text-red-300"
                            title="Débloquer"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-zinc-400 truncate mt-1">
                        {slot.reason || 'Bloqué'}
                      </div>
                      {slot.is_automatic && (
                        <div className="text-blue-400 text-[10px] mt-1">
                          Auto (réservation)
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Empty state */}
                  {dayBookings.length === 0 && slots.length === 0 && (
                    <div className="text-center py-4 text-zinc-600 text-xs">
                      Disponible
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-500/20 border border-emerald-500/30"></div>
          <span className="text-zinc-400">Réservation confirmée</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500/20 border border-yellow-500/30"></div>
          <span className="text-zinc-400">Réservation en attente</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/30"></div>
          <span className="text-zinc-400">Blocage auto (1h30)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500/20 border border-red-500/30"></div>
          <span className="text-zinc-400">Blocage manuel</span>
        </div>
      </div>

      {/* All Blocked Slots List */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">
          Tous les blocages manuels
        </h3>
        
        {blockedSlots.filter(s => !s.is_automatic).length === 0 ? (
          <p className="text-zinc-500 text-center py-4">
            Aucun blocage manuel pour cette période
          </p>
        ) : (
          <div className="space-y-2">
            {blockedSlots.filter(s => !s.is_automatic).map(slot => (
              <div 
                key={slot.id}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <div className="text-white font-medium">
                      {format(parseLocalTime(slot.start_datetime), 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                    <div className="text-zinc-400 text-sm">
                      {slot.is_full_day 
                        ? 'Journée entière' 
                        : `${format(parseLocalTime(slot.start_datetime), 'HH:mm')} - ${format(parseLocalTime(slot.end_datetime), 'HH:mm')}`
                      }
                      {slot.reason && ` • ${slot.reason}`}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteSlot(slot.id)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Unlock className="w-4 h-4 mr-1" />
                  Débloquer
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Slot Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="glass-card rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white font-['Outfit'] mb-6">
              Bloquer un créneau
            </h3>
            
            <form onSubmit={handleAddSlot} className="space-y-4">
              <div>
                <Label className="text-zinc-300">Date</Label>
                <Input
                  type="date"
                  value={newSlot.date}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="fullDay"
                  checked={newSlot.isFullDay}
                  onChange={(e) => setNewSlot({ ...newSlot, isFullDay: e.target.checked })}
                  className="w-4 h-4 rounded border-zinc-600"
                />
                <Label htmlFor="fullDay" className="text-zinc-300 cursor-pointer">
                  Journée entière
                </Label>
              </div>
              
              {!newSlot.isFullDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Heure début</Label>
                    <Input
                      type="time"
                      value={newSlot.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Heure fin</Label>
                    <Input
                      type="time"
                      value={newSlot.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-zinc-300">Raison (optionnel)</Label>
                <Input
                  type="text"
                  value={newSlot.reason}
                  onChange={(e) => setNewSlot({ ...newSlot, reason: e.target.value })}
                  placeholder="Ex: Rendez-vous personnel"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border-zinc-700 text-zinc-300"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Bloquer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvailabilityManager;
